import { useQuery } from '@tanstack/react-query';
import { Code2 } from 'lucide-react';

import { getNodeSource } from '../api/source.js';
import { useApi } from '../api/use-api.js';
import { cn } from '../lib/cn.js';
import type { SigmaNodeAttributes } from '../lib/graph-node-attributes.js';

interface SourceSectionProps {
  readonly slug: string;
  readonly attr: SigmaNodeAttributes;
  readonly selectedNodeId: string;
  /** Optional branch override — when undefined, server defaults to the
   *  registry's `defaultBranch` for the slug (260525-1509 fix). */
  readonly branch?: string;
}

export function SourceSection({
  slug,
  attr,
  selectedNodeId,
  branch,
}: SourceSectionProps): JSX.Element {
  const api = useApi();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['node-source', slug, selectedNodeId, branch ?? null],
    queryFn: () =>
      getNodeSource(api, slug, attr.filePath!, attr.startLine ?? 1, branch ? { branch } : {}),
    enabled: Boolean(attr.filePath),
    staleTime: 300_000,
  });

  return (
    <div className="flex min-h-0 w-1/2 flex-col border-r border-border-subtle">
      <div className="flex items-center gap-1.5 px-4 py-1.5 text-[10px] text-text-muted">
        <Code2 className="h-3 w-3" />
        <span className="uppercase tracking-widest">Source</span>
      </div>
      <div className="min-h-0 flex-1 overflow-auto">
        {!attr.filePath && (
          <p className="px-4 py-3 font-mono text-[11px] text-text-muted">No file path available.</p>
        )}
        {attr.filePath && isLoading && (
          <p className="px-4 py-3 font-mono text-[11px] text-text-muted">Loading…</p>
        )}
        {attr.filePath && isError && (
          <p className="px-4 py-3 font-mono text-[11px] text-text-muted">Source unavailable.</p>
        )}
        {data && (
          <pre className="overflow-x-auto px-4 pb-3 font-mono text-[11px] leading-relaxed text-text-secondary">
            {data.lines.map((line, i) => {
              const lineNo = data.startLine + i;
              const isTarget = attr.startLine !== undefined && lineNo === attr.startLine;
              return (
                <div
                  key={lineNo}
                  className={cn('flex gap-3', isTarget && 'bg-accent/15 text-text-primary')}
                >
                  <span className="w-8 flex-shrink-0 select-none text-right text-text-muted">
                    {lineNo}
                  </span>
                  <span>{line}</span>
                </div>
              );
            })}
          </pre>
        )}
      </div>
    </div>
  );
}
